import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  FormControlSizes,
  HEDGE_ORDER_STATUS,
  HedgeOrderStatus,
  IconName,
  MixpanelEvent,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useHedgeOrderStatusColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type Column,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import { getTradingOrderDetailsRoute } from '../../Routes/routes';
import {
  onlyServerFilterKeys,
  useHedgeOrderStatusFilter,
  type HedgeOrderStatusFilter,
} from './useHedgeOrderStatusFilter';

export interface HedgeOrderStatusBlotterTableProps {
  /** Unique id per blotter instance */
  blotterID: string;
  /** ID for common blotter type persistence; usually a subset of the blotterId to represent blotter area 'type' */
  blotterPersistID?: string;
  tabLabel?: string;
  orderID?: string;

  defaultColumns?: (Column | keyof HedgeOrderStatus)[];
  defaultFilter?: HedgeOrderStatusFilter;
  persistFilter?: boolean;

  initialIsOpen?: boolean;

  onCloneTab?: () => void;
  generateOrderDetailsRoute?: GenerateOrderDetailsRoute;
}

export function HedgeOrderStatusBlotterTable({
  tabLabel,
  orderID,
  blotterID,
  blotterPersistID = blotterID,
  defaultColumns,
  persistFilter,
  initialIsOpen,
  onCloneTab,
  generateOrderDetailsRoute = getTradingOrderDetailsRoute,
}: HedgeOrderStatusBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();

  const permanentFilters = useMemo<HedgeOrderStatusFilter>(
    () => ({
      InitiatingOrderID: orderID,
    }),
    [orderID]
  );

  const columnDefinitions = useHedgeOrderStatusColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterPersistID, undefined, {
    persistFilter,
  });

  const filterResults = useHedgeOrderStatusFilter({
    persistedBlotterTable,
    permanentFilters,
  });
  const { clientSideFilter: clientLocalFilter, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { showBlotterPauseButton } = useFeatureFlag();

  const request = useMemo(
    () => ({
      name: HEDGE_ORDER_STATUS,
      tag: blotterID,
      ...(orderID != null && { InitiatingOrderID: orderID }),
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, orderID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: HedgeOrderStatus.rowID,
    clientLocalFilter,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    columns: columnDefinitions,
    persistence: persistedBlotterTable,
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'HedgeOrderStatuses',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      <BlotterTable {...blotterTable} />
    </>
  );
}
